<!-- Outer modal: AG-Grid list of Stream entries + Add / Done. z-50 so it
     stacks on top of the Scanner edit modal that opened it. -->
<div *ngIf="showModal"
     class="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
     (click)="onBackdropClick($event, 'outer')">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg w-[1280px] max-w-[95vw] max-h-[90vh] flex flex-col border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="py-3 px-6 dark:bg-app-black-100 rounded-t flex items-center justify-between gap-3">
      <h2 class="text-sm font-semibold text-gray-900 dark:text-white">
        <i class="fa-solid fa-tower-broadcast text-app-teal-100 mr-2"></i>
        Stream editor
        <span *ngIf="scannerId" class="text-[11px] text-gray-500 dark:text-gray-400 ml-2 font-mono">{{ scannerId }}</span>
      </h2>
      <div class="flex items-center gap-2">
        <button type="button" (click)="openAdd()"
                class="text-xs uppercase tracking-wide text-app-teal-100 hover:text-app-teal-200 border border-app-teal-100/60 hover:border-app-teal-100 rounded px-3 py-1 flex items-center gap-1">
          <i class="fa-solid fa-plus text-[10px]"></i>
          Add stream
        </button>
        <button type="button" (click)="onDone()"
                class="text-xs uppercase tracking-wide text-gray-500 hover:text-gray-300 border border-gray-300 dark:border-[#434863] hover:border-gray-400 rounded px-3 py-1">
          Done
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-hidden px-6 py-4 flex flex-col gap-3">
      <p *ngIf="entries.length === 0"
         class="text-xs text-gray-500 dark:text-gray-400 italic">
        No Stream entries. Click <strong>Add stream</strong> to define a WebSocket / REST route.
      </p>

      <!-- AG-Grid list of entries. Row click = edit; drag handle = reorder.
           Explicit h-[400px] because flex-1 on the <ag-grid-angular class>
           didn't resolve a height in the modal flex context (cid 067b02ee —
           Mike's screenshot showed an invisible grid above the row-strip). -->
      <div *ngIf="entries.length > 0" class="flex flex-col gap-1">
        <ag-grid-angular
          class="w-full h-[400px]"
          [theme]="gridTheme"
          [gridOptions]="gridOptions"
          [rowData]="entries"
          (rowClicked)="onRowClicked($event)"
          (rowDragEnd)="onRowDragEnd($event)">
        </ag-grid-angular>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 italic">
          Click any row to edit · drag the <i class="fa-solid fa-grip-vertical"></i> handle to reorder · remove inside the edit popup.
        </p>
      </div>
    </div>
  </div>
</div>

<!-- Sub-modal: single-entry form. z-60 so it stacks above the outer list.
     Opens when editingIndex is non-null (either an existing row or -1 for a
     fresh entry pending save on confirm). -->
<div *ngIf="showModal && editingIndex !== null"
     class="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
     (click)="onBackdropClick($event, 'inner')">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg w-[640px] max-w-[95vw] max-h-[90vh] flex flex-col border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="py-3 px-6 dark:bg-app-black-100 rounded-t flex items-center justify-between gap-3">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
        <i class="fa-solid fa-pen-to-square text-app-teal-100 mr-2"></i>
        {{ editingIndex === -1 ? 'Add stream entry' : 'Edit stream entry #' + ((editingIndex || 0) + 1) }}
      </h3>
      <button type="button" (click)="cancelEdit()"
              class="text-gray-500 hover:text-gray-300 text-sm" aria-label="Close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <label class="flex flex-col gap-1 md:col-span-1">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Collection *
            <span *ngIf="draftCollectionError()" class="text-red-500 normal-case ml-1">({{ draftCollectionError() }})</span>
          </span>
          <input type="text" [(ngModel)]="editingDraft.Collection"
                 name="draft_collection"
                 placeholder="mongo collection name"
                 class="bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1 text-xs font-mono dark:text-white"
                 [class.border-red-500]="draftCollectionError()"
                 [class.border-gray-300]="!draftCollectionError()"
                 [class.dark:border-gray-600]="!draftCollectionError()" />
        </label>

        <label class="flex flex-col gap-1 md:col-span-1">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Channel</span>
          <input type="text" [(ngModel)]="editingDraft.Channel"
                 name="draft_channel"
                 placeholder="redis channel, e.g. tt:volsuite:gex"
                 class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
        </label>

        <label class="flex flex-col gap-1 md:col-span-1">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            WsPath
            <span *ngIf="draftWsPathError()" class="text-red-500 normal-case ml-1">({{ draftWsPathError() }})</span>
          </span>
          <input type="text" [(ngModel)]="editingDraft.WsPath"
                 name="draft_wspath"
                 placeholder="e.g. tt/volsuite/gex"
                 class="bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1 text-xs font-mono dark:text-white"
                 [class.border-red-500]="draftWsPathError()"
                 [class.border-gray-300]="!draftWsPathError()"
                 [class.dark:border-gray-600]="!draftWsPathError()" />
        </label>

        <label class="flex flex-col gap-1 md:col-span-1">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            RestPath
            <span *ngIf="draftRestPathError()" class="text-red-500 normal-case ml-1">({{ draftRestPathError() }})</span>
          </span>
          <input type="text" [(ngModel)]="editingDraft.RestPath"
                 name="draft_restpath"
                 placeholder="e.g. tt-vol/gex (omit if WS-only)"
                 class="bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1 text-xs font-mono dark:text-white"
                 [class.border-red-500]="draftRestPathError()"
                 [class.border-gray-300]="!draftRestPathError()"
                 [class.dark:border-gray-600]="!draftRestPathError()" />
        </label>

        <label class="flex flex-col gap-1 md:col-span-1">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">KeyField</span>
          <input type="text" [(ngModel)]="editingDraft.KeyField"
                 name="draft_keyfield"
                 placeholder="doc field for push cache (default: symbol)"
                 class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
        </label>

        <label class="flex items-center gap-2 md:col-span-1 md:self-end md:pb-1">
          <input type="checkbox" [(ngModel)]="editingDraft.Enabled" name="draft_enabled" />
          <span class="text-[11px] font-medium text-gray-700 dark:text-gray-300">Enabled</span>
        </label>
      </div>

      <!-- Advanced panel toggle -->
      <div class="mt-3">
        <button type="button" (click)="toggleDraftExtras()"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 flex items-center gap-1">
          <i class="fa-solid text-[9px]"
             [class.fa-chevron-down]="draftExpandedExtras"
             [class.fa-chevron-right]="!draftExpandedExtras"></i>
          Advanced
        </button>
        <div *ngIf="draftExpandedExtras"
             class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs border border-gray-200 dark:border-[#434863] rounded p-3 bg-gray-50/40 dark:bg-app-black-200/40">
          <label class="flex flex-col gap-1 md:col-span-1">
            <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">SourceDb</span>
            <input type="text" [(ngModel)]="editingDraft.SourceDb" name="draft_sourcedb"
                   placeholder="e.g. F2-TrendLabs"
                   class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
          </label>
          <label class="flex flex-col gap-1 md:col-span-1">
            <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">SourceDbPattern</span>
            <input type="text" [(ngModel)]="editingDraft.SourceDbPattern" name="draft_sourcedbpattern"
                   placeholder="e.g. OS_LIVE-{ET-date}"
                   class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
          </label>
          <label class="flex flex-col gap-1 md:col-span-2">
            <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">SymbolListName</span>
            <input type="text" [(ngModel)]="editingDraft.SymbolListName" name="draft_symbollistname"
                   placeholder="named symbol list to filter stream against (optional)"
                   class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
          </label>
          <label class="flex items-center gap-2">
            <input type="checkbox" [(ngModel)]="editingDraft.SkipPremiumFloor" name="draft_skippremiumfloor" />
            <span class="text-[11px] font-medium text-gray-700 dark:text-gray-300">SkipPremiumFloor</span>
          </label>
          <label class="flex items-center gap-2">
            <input type="checkbox" [(ngModel)]="editingDraft.Append" name="draft_append" />
            <span class="text-[11px] font-medium text-gray-700 dark:text-gray-300">Append (event-stream)</span>
          </label>
          <label class="flex items-center gap-2 md:col-span-2">
            <input type="checkbox" [(ngModel)]="editingDraft.Legacy" name="draft_legacy" />
            <span class="text-[11px] font-medium text-gray-700 dark:text-gray-300">Legacy (skip __schema__ sentinel check)</span>
          </label>
        </div>
      </div>
    </div>

    <div class="py-3 px-6 border-t border-gray-200 dark:border-[#434863] flex items-center gap-2">
      <!-- IT-F2-406 cid 067b02ee — Delete lives inside the edit modal so
           the outer grid can be read-only + drag-only on destructive action.
           Hidden for the "new entry" draft (editingIndex === -1). -->
      <button *ngIf="editingIndex !== -1"
              type="button" (click)="deleteFromEdit()"
              class="text-xs text-red-500 hover:text-red-400 border border-red-500/50 hover:border-red-500 rounded px-3 py-1 flex items-center gap-1"
              title="Remove this stream entry">
        <i class="fa-solid fa-trash text-[10px]"></i>
        Delete
      </button>
      <div class="flex-1"></div>
      <button type="button" (click)="cancelEdit()"
              class="dark-btn-secondary text-xs px-3 py-1">cancel</button>
      <button type="button" (click)="saveEdit()"
              [disabled]="!canSaveDraft()"
              class="dark-btn-primary text-xs px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed">
        {{ editingIndex === -1 ? 'Add' : 'Save' }}
      </button>
    </div>
  </div>
</div>
